import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { supabase } from '../lib/supabase';
// REMOVED: Complex aiProviderService import - using direct server calls only
export interface AIMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
}
import { format } from 'date-fns';
import { env } from '../config/environment';
// Import Meeting model and service
import { Meeting } from '../types/meetings';
import { MeetingsService } from '../services/meetingsService';

import contextPersistenceService from '../services/contextPersistenceService';
const { safeLog } = require('../utils/safeLog.js');


// Use ANON key for client-side operations  
const { supabaseUrl, supabaseAnonKey } = env;

// FIX: Always use production API - no localhost URLs allowed in any environment
function getApiUrl(): string {
  // Force production API URL - abandon all localhost usage
  const apiUrl = 'https://mobile.padrinho.app';
  
  // Log if we're overriding a localhost configuration
  const envUrl = env.apiUrl || process.env.EXPO_PUBLIC_API_URL;
  if (envUrl && (
    envUrl.includes('localhost') || 
    envUrl.includes('127.0.0.1') || 
    envUrl.startsWith('http://10.') || 
    envUrl.startsWith('http://192.168.')
  )) {
    safeLog.info('🔧 ConversationService: Forcing production API, overriding local URL:', envUrl, '→', apiUrl);
  }
  
  return apiUrl;
}

const apiUrl = getApiUrl();

// Add safety check for environment
if (!apiUrl) {
  safeLog.warn('⚠️ API URL not configured, conversation service may not work properly');
}

export interface ConversationContext {
  userId: string;
  userName?: string;
  sobrietyDays: number;
  setupType?: 'starting-today' | 'existing-days' | 'relapse';
  recentNotes?: string[];
  meetingInterests?: string[];
  currentMood?: string;
  conversationHistory: ConversationMessage[];
  lastInteraction?: Date;
  sessionGoals?: string[];
  recentConversationThemes?: string[];
  recentMoods?: Array<{
    sentiment: string;
    note?: string;
    date: string;
  }>;
  recentMeetings?: Array<{
    name: string;
    type: string;
    savedAt: string;
  }>;
}

export interface ConversationMessage {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  timestamp: Date;
  context?: {
    sobrietyDays?: number;
    mood?: string;
    supportType?: 'encouragement' | 'advice' | 'crisis' | 'celebration' | 'general';
  };
}

export interface ConversationResponse {
  message: string;
  supportType: 'encouragement' | 'advice' | 'crisis' | 'celebration' | 'general';
  suggestedActions?: string[];
  contextUpdates?: Partial<ConversationContext>;
  meetingsData?: any;
}

class ConversationService {
  private static instance: ConversationService;
  private contexts: Map<string, ConversationContext> = new Map();
  private readonly CONTEXT_STORAGE_KEY = 'bill_conversation_context';
  private readonly MAX_HISTORY_LENGTH = 20;
  private readonly MAX_TOKENS_PER_REQUEST = 6000; // Conservative limit for API requests
  private readonly SUMMARIZATION_THRESHOLD = 12; // Summarize when we have 12+ messages
  private readonly CONTEXT_TIMEOUT = 24 * 60 * 60 * 1000; // 24 hours

  static getInstance(): ConversationService {
    if (!ConversationService.instance) {
      ConversationService.instance = new ConversationService();
    }
    return ConversationService.instance;
  }

  async initializeContext(userId: string): Promise<ConversationContext> {
    // Load existing context or create new one
    let context = await this.loadContext(userId);

    if (!context || this.isContextExpired(context)) {
      context = await this.createFreshContext(userId);
    }

    // Update with latest user data
    context = await this.enrichContextWithUserData(context);

    this.contexts.set(userId, context);
    await this.saveContext(context);

    return context;
  }

  async sendMessage(
    userId: string,
    message: string,
    additionalContext?: Partial<ConversationContext> & {
      batchedMessages?: string[];
      messageCount?: number;
      accessToken?: string;
    }
  ): Promise<ConversationResponse> {
    const context = await this.getOrInitializeContext(userId);

    // DISABLED: Local meeting handling logic - let server handle ALL meeting queries for consistency
    // The server has comprehensive meeting data access and anti-hallucination measures
    // All meeting queries should go through the server API for accurate, real-time responses
    if (false && this.isMeetingRelatedQuery(message)) {
      // [COMMENTED OUT] Local meeting handling logic removed to prevent stale data
      // All meeting queries now go to server which has full database access
    }

    // Add user message to history
    const userMessage: ConversationMessage = {
      id: `user_${Date.now()}`,
      content: message,
      role: 'user',
      timestamp: new Date(),
      context: {
        sobrietyDays: context.sobrietyDays,
        mood: additionalContext?.currentMood || context.currentMood
      }
    };

    context.conversationHistory.push(userMessage);
    context.lastInteraction = new Date();

    // Apply additional context if provided
    if (additionalContext) {
      Object.assign(context, additionalContext);
    }

    // Manage conversation history: summarize if too long or exceeding token limits
    await this.manageConversationHistory(context);

    // Generate enhanced system prompt with context
    const systemPrompt = await this.buildEnhancedSystemPrompt(context);

    // Prepare conversation history for API
    const conversationHistory = this.prepareConversationHistory(context);

    try {
      safeLog.info('🔄 Conversation Service: Sending to AI Provider...', {
        userId,
        messageLength: message.length,
        sobrietyDays: context.sobrietyDays,
        historyLength: conversationHistory.length,
        hasSystemPrompt: !!systemPrompt,
        contextKeys: Object.keys(context),
        timestamp: new Date().toISOString()
      });

      // Prepare messages for AI provider (ensure proper typing)
      const messages: AIMessage[] = [
        ...conversationHistory.map(msg => ({
          role: msg.role as 'user' | 'assistant' | 'system',
          content: msg.content
        })),
        { role: 'user' as const, content: message }
      ];

      // Get current user's JWT token - prefer passed token, fallback to Supabase auth
      safeLog.info('🔐 ConversationService: Getting user authentication token...');
      let accessToken = additionalContext?.accessToken;
      
      if (!accessToken) {
        safeLog.info('🔐 ConversationService: No token provided, falling back to getSession()...');
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          safeLog.error('❌ ConversationService: Failed to get auth session:', sessionError);
          throw new Error('Authentication failed: Unable to get user session');
        }
        
        if (!session || !session.access_token) {
          safeLog.error('❌ ConversationService: No valid session or access token found');
          throw new Error('Authentication required: Please log in to continue chatting with Bill');
        }
        accessToken = session.access_token;
      } else {
        safeLog.info('🔐 ConversationService: Using provided access token');
      }

      // SIMPLIFIED: Direct server API call using validated environment config with JWT authentication
      safeLog.info('📡 ConversationService: Using API URL from environment:', apiUrl);
      safeLog.info('📡 ConversationService: Making authenticated server call to:', `${apiUrl}/api/bill/chat`);
      safeLog.info('🔐 ConversationService: Including JWT token for user:', userId);
      
      // Add 30-second timeout for mobile network reliability
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
        safeLog.info('⏰ ConversationService: Request timeout after 30 seconds');
      }, 30000);
      
      let response: any;
      
      try {
        const serverResponse = await fetch(`${apiUrl}/api/bill/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            message,
            userId: context.userId,
            context: {
              conversationHistory: conversationHistory,
              sobrietyDays: context.sobrietyDays,
              currentMood: context.currentMood,
              recentStruggle: this.detectRecentStruggle(context)
            }
          }),
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!serverResponse.ok) {
          // Handle authentication errors specifically
          if (serverResponse.status === 401) {
            safeLog.error('❌ ConversationService: Authentication failed (401)');
            throw new Error('Sua sessão expirou. Por favor, faça login novamente para continuar conversando com o Bill.');
          } else if (serverResponse.status === 403) {
            safeLog.error('❌ ConversationService: Access forbidden (403)');
            throw new Error('Você não tem permissão para acessar este recurso. Verifique sua conta ou contate o suporte.');
          } else {
            safeLog.error(`❌ ConversationService: Server error ${serverResponse.status}:`, serverResponse.statusText);
            throw new Error(`Erro do servidor: ${serverResponse.status} ${serverResponse.statusText}`);
          }
        }

        response = await serverResponse.json();
      } catch (error: any) {
        clearTimeout(timeoutId);
        
        // Handle different types of errors
        if (error.name === 'AbortError') {
          safeLog.error('❌ ConversationService: Request timed out after 30 seconds');
          throw new Error('A conexão com o Bill demorou muito para responder. Verifique sua internet e tente novamente.');
        } else if (error.message && error.message.includes('Network request failed')) {
          safeLog.error('❌ ConversationService: Network error:', error);
          throw new Error('Erro de rede. Verifique sua conexão com a internet e tente novamente.');
        } else if (error.message && (error.message.includes('Sua sessão expirou') || error.message.includes('Authentication') || error.message.includes('Erro do servidor'))) {
          // Re-throw our custom error messages
          throw error;
        } else {
          safeLog.error('❌ ConversationService: Unexpected error:', error);
          throw new Error('Ocorreu um erro inesperado ao conectar com o Bill. Tente novamente em alguns instantes.');
        }
      }

      if (!response || !response.message) {
        throw new Error('Empty or invalid response from AI provider');
      }

      safeLog.info('✅ Conversation Service: Received response from AI Provider', {
        messageLength: response.message.length,
        supportType: response.supportType,
        provider: response.provider,
        model: response.model,
        hasMessage: !!response.message,
        messagePreview: response.message.substring(0, 100) + '...',
        messageComplete: response.message,
        timestamp: new Date().toISOString(),
        hasTripleSeparator: response.message.includes('|||'),
        splitMessages: response.message.split('|||').length
      });

      // Add assistant response to history
      const assistantMessage: ConversationMessage = {
        id: `assistant_${Date.now()}`,
        content: response.message,
        role: 'assistant',
        timestamp: new Date(),
        context: {
          supportType: response.supportType
        }
      };

      context.conversationHistory.push(assistantMessage);

      safeLog.info('💾 Conversation Service: Message exchange completed', {
        userMessageId: userMessage.id,
        assistantMessageId: assistantMessage.id,
        userMessageLength: userMessage.content.length,
        assistantMessageLength: assistantMessage.content.length,
        totalHistoryLength: context.conversationHistory.length,
        timestamp: new Date().toISOString()
      });

      // Update context and save
      this.contexts.set(userId, context);
      await this.saveContext(context);

      safeLog.info('✅ Conversation Service: Context updated and saved', {
        userId,
        historyLength: context.conversationHistory.length,
        lastInteraction: context.lastInteraction
      });

      // Generate suggested actions based on conversation
      const suggestedActions = this.generateSuggestedActions(context, response);

      const conversationResponse = {
        message: response.message,
        supportType: response.supportType,
        suggestedActions,
        contextUpdates: {
          lastInteraction: context.lastInteraction,
          currentMood: this.extractMoodFromConversation(message, response.message)
        }
      };

      safeLog.info('✅ Conversation Service: Created response object', {
        hasMessage: !!conversationResponse.message,
        messageLength: conversationResponse.message?.length || 0,
        supportType: conversationResponse.supportType,
        actionsCount: conversationResponse.suggestedActions.length,
        hasContextUpdates: !!conversationResponse.contextUpdates
      });

      return conversationResponse;

    } catch (error) {
      safeLog.error('❌ Conversation Service: Error in sendMessage:', {
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        userId,
        messageLength: message.length,
        contextValid: !!context,
        historyLength: context?.conversationHistory?.length || 0,
        timestamp: new Date().toISOString()
      });

      // Check if it's an authentication error
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('Authentication') || errorMessage.includes('login') || errorMessage.includes('sessão expirou')) {
        // Return authentication-specific error response
        return {
          message: errorMessage,
          supportType: 'general' as const,
          suggestedActions: ['Fazer login novamente', 'Verificar conexão'],
          contextUpdates: {
            lastInteraction: new Date()
          }
        };
      }

      // Return a general fallback response for other errors
      return {
        message: 'Desculpe, estou com dificuldades técnicas no momento. Tente novamente em alguns segundos.',
        supportType: 'general' as const,
        suggestedActions: ['Tentar novamente', 'Verificar conexão'],
        contextUpdates: {
          lastInteraction: new Date()
        }
      };
    }
  }

  // Summarizes conversation history if it exceeds the threshold
  private async summarizeConversation(context: ConversationContext): Promise<void> {
    safeLog.info(`💬 Conversation Service: Summarizing conversation (history length: ${context.conversationHistory.length})...`);

    const historyToSummarize = context.conversationHistory.slice(0, -2); // Exclude last user message and assistant response

    if (historyToSummarize.length < this.SUMMARIZATION_THRESHOLD) {
      safeLog.info('✅ Conversation Service: History below summarization threshold.');
      return;
    }

    const formattedHistory = historyToSummarize.map(msg => `${msg.role}: ${msg.content}`).join('\n');

    // For now, use a simple summary approach without external AI provider
    // since all AI operations now go through the server API
    safeLog.info('📝 Conversation Service: Creating simple conversation summary...');
    
    const summaryMessage: ConversationMessage = {
      id: `summary_${Date.now()}`,
      content: `[Resumo]: Conversa anterior com ${historyToSummarize.length} mensagens sobre recuperação e apoio.`,
      role: 'assistant',
      timestamp: new Date(),
      context: {
        supportType: 'general'
      }
    };

    // Replace the oldest messages with the summary
    context.conversationHistory.splice(0, historyToSummarize.length, summaryMessage);
    
    safeLog.info('✅ Conversation Service: Conversation summarized successfully.', {
      newHistoryLength: context.conversationHistory.length,
      summary: summaryMessage.content
    });
  }

  // Manages conversation history by trimming and summarizing
  private async manageConversationHistory(context: ConversationContext): Promise<void> {
    // Trim history if it exceeds the max length
    if (context.conversationHistory.length > this.MAX_HISTORY_LENGTH) {
      const removedCount = context.conversationHistory.length - this.MAX_HISTORY_LENGTH;
      context.conversationHistory = context.conversationHistory.slice(-this.MAX_HISTORY_LENGTH);
      safeLog.info(`🧹 Conversation Service: Trimmed ${removedCount} old messages from history.`);
    }

    // If history is long enough, try to summarize
    if (context.conversationHistory.length >= this.SUMMARIZATION_THRESHOLD) {
      await this.summarizeConversation(context);
    }
  }


  private async createFreshContext(userId: string): Promise<ConversationContext> {
    return {
      userId,
      sobrietyDays: 0,
      conversationHistory: [],
      lastInteraction: new Date(),
      sessionGoals: []
    };
  }

  private async enrichContextWithUserData(context: ConversationContext): Promise<ConversationContext> {
    // DISABLED: Direct database access - let server handle ALL user data queries for consistency
    // The server has comprehensive database fixes and proper schema alignment
    // Local database queries may use incorrect schema or bypass server corrections
    safeLog.info('📋 ConversationService: Skipping local user data enrichment - server handles this');
    
    // All user context enrichment now happens on the server side for consistency
    return context;
  }

  private extractConversationThemes(conversations: any[]): string[] {
    const themes: string[] = [];
    const themeKeywords = {
      'sobriedade': ['dias sóbrio', 'sobriedade', 'parar de beber', 'abstinência'],
      'ansiedade': ['ansioso', 'preocupado', 'nervoso', 'ansiedade'],
      'tristeza': ['triste', 'deprimido', 'melancolia', 'down'],
      'vontade': ['vontade de beber', 'tentação', 'crave', 'desejo'],
      'celebração': ['parabéns', 'conquista', 'vitória', 'orgulho'],
      'recaída': ['recaída', 'bebi', 'escorregão', 'voltei a beber'],
      'apoio': ['ajuda', 'apoio', 'conversar', 'desabafo']
    };

    conversations.forEach(conv => {
      const text = (conv.user_message + ' ' + conv.bill_response).toLowerCase();

      Object.entries(themeKeywords).forEach(([theme, keywords]) => {
        if (keywords.some(keyword => text.includes(keyword)) && !themes.includes(theme)) {
          themes.push(theme);
        }
      });
    });

    return themes.slice(0, 3); // Return top 3 themes
  }

  private async buildEnhancedSystemPrompt(context: ConversationContext): Promise<string> {
    // Load personality configuration from database
    let personalityPrompt = `Você é "Bill", um assistente virtual especializado em apoiar membros da plataforma Padrinho que enfrentam problemas com alcoolismo e outras dependências. Seu conhecimento abrange o caminho dos 12 passos, psicologia e comportamento humano.

PRIORIDADE MÁXIMA: RESPONDA DIRETAMENTE AO QUE O USUÁRIO ESTÁ DIZENDO
- SEMPRE leia e compreenda a mensagem atual do usuário
- Responda especificamente ao que ele perguntou ou comentou
- Use o contexto (diário, sobriedade, etc.) apenas como informação de apoio, NÃO como foco principal
- Se o usuário fez uma pergunta, responda a pergunta
- Se o usuário contou algo, comente sobre o que ele contou
- Se o usuário expressou um sentimento, reconheça esse sentimento

VOCÊ DEVE RESPONDER COM MÚLTIPLAS MENSAGENS SEPARADAS, como um amigo real faria no WhatsApp. Use "|||" para separar cada mensagem.

MARCOS DE SOBRIEDADE - Quando celebrar conquistas:
- Seja extremamente entusiasmado e caloroso ao parabenizar
- Use emojis de celebração (🎉, 🏆, 👏, 💪, ⭐)
- Mencione a importância específica desse marco
- Reconheça o esforço e determinação necessários
- Incentive a continuar e defina a próxima meta
- Para marcos iniciais (1 dia, 3 dias): foque na coragem de começar
- Para marcos semanais/mensais: destaque a consistência e força
- Para marcos maiores (3+ meses): celebre a transformação de vida`;

    try {
      const { data: billConfig, error } = await supabase
        .from('bill_config')
        .select('system_prompt, personality_type')
        .eq('config_type', 'main')
        .single();

      if (!error && billConfig?.system_prompt) {
        personalityPrompt = billConfig.system_prompt;
        safeLog.info('✅ ConversationService: Loaded personality config', {
          personalityType: billConfig.personality_type || 'default',
          promptLength: personalityPrompt.length
        });
      } else {
        safeLog.info('⚠️ ConversationService: Using default prompt', { error: error?.message });
      }
    } catch (error) {
      safeLog.error('❌ ConversationService: Error loading personality config:', error);
    }

    let contextualInfo = '';

    if (context.userName) {
      contextualInfo += `O usuário se chama ${context.userName}. `;
    }

    if (context.sobrietyDays !== undefined) {
      contextualInfo += `Está há ${context.sobrietyDays} dias sóbrio. `;
    }

    if (context.setupType) {
      const setupMessages = {
        'starting-today': 'Começou sua jornada de sobriedade hoje. ',
        'existing-days': 'Já estava em recuperação quando se juntou à plataforma. ',
        'relapse': 'Passou por uma recaída e está recomeçando. '
      };
      contextualInfo += setupMessages[context.setupType];
    }

    // Add conversation continuity
    if (context.conversationHistory.length > 2) {
      contextualInfo += `Vocês já conversaram antes. Continue a conversa naturalmente, focando no que ele está dizendo agora. `;
    }

    // Background context (use only when relevant to current conversation)
    let backgroundContext = '';

    if (context.recentNotes && context.recentNotes.length > 0) {
      backgroundContext += `[Informação de apoio] Notas recentes: "${context.recentNotes.join('", "')}" `;
    }

    if (context.currentMood) {
      backgroundContext += `[Informação de apoio] Humor atual: ${context.currentMood}. `;
    }

    if (context.recentMoods && context.recentMoods.length > 0) {
      const moodSummary = context.recentMoods.map(mood =>
        `${mood.sentiment}${mood.note ? ` (${mood.note})` : ''}`
      ).join(', ');
      backgroundContext += `[Informação de apoio] Estados emocionais recentes: ${moodSummary}. `;
    }

    if (context.recentMeetings && context.recentMeetings.length > 0) {
      const meetingTypes = [...new Set(context.recentMeetings.map(m => m.type))];
      backgroundContext += `[Informação de apoio] Reuniões salvas: ${meetingTypes.join(', ')}. `;
    }

    if (backgroundContext) {
      contextualInfo += `\n\nINFORMAÇÕES DE APOIO (use apenas se relevante para a conversa atual): ${backgroundContext}`;
    }

    // Load persistent context for long-term memory
    try {
      const persistentContext = await contextPersistenceService.loadPersistentContext(context.userId);
      if (persistentContext) {
        const contextSummary = `
CONTEXTO PERSISTENTE - Use esta informação para personalizar suas respostas:
- Tópicos importantes: ${persistentContext.keyTopics.join(', ')}
- Padrões emocionais recentes: ${persistentContext.emotionalPatterns.map(p => `${p.pattern} (${p.frequency}x)`).join(', ')}
- Eventos importantes: ${persistentContext.importantEvents.slice(-3).map(e => e.event).join('; ')}
- Estilo de apoio preferido: ${persistentContext.preferredSupport.style}
- Estratégias eficazes: ${persistentContext.preferredSupport.effectiveStrategies.join(', ')}
- Nível de confiança: ${persistentContext.relationshipDynamics.trustLevel}/10
`;
        contextualInfo += contextSummary;
        safeLog.info('✅ ConversationService: Loaded persistent context', {
          keyTopics: persistentContext.keyTopics.length,
          emotionalPatterns: persistentContext.emotionalPatterns.length,
          importantEvents: persistentContext.importantEvents.length
        });
      }
    } catch (error) {
      safeLog.error('❌ ConversationService: Error loading persistent context:', error);
    }

    const guidelines = `
FORMATO OBRIGATÓRIO - USE "|||" PARA SEPARAR MENSAGENS:
- Divida sua resposta em 2-4 mensagens curtas
- Cada mensagem deve ter 15-35 palavras
- Use "|||" entre cada mensagem
- Primeira mensagem: responda diretamente ao que o usuário disse
- Mensagens seguintes: aprofunde a conversa sobre o tópico que ELE trouxe

EXEMPLO DE FORMATO:
"${context.userName ? context.userName + ', ' : ''}entendo o que você tá passando...|||Essa vontade é bem comum mesmo, faz parte do processo.|||Mas me conta, o que costuma funcionar pra você quando isso acontece?|||Tô aqui contigo, vamos passar por isso juntos."

REGRAS DE CONVERSA - ORDEM DE PRIORIDADE:
1. PRIMEIRO: Responda diretamente à mensagem atual do usuário
2. SEGUNDO: Continue a conversa sobre o que ELE trouxe
3. TERCEIRO: Use informações de contexto apenas se relevante para o que ele disse
4. ÚLTIMO: Só mude de assunto se ele não estiver falando de nada específico

ESTILO DE CONVERSA:
• Seja um amigo próximo e experiente que ESCUTA
• Use linguagem natural e brasileira
• ${context.userName ? `Chame pelo nome (${context.userName})` : 'Use tratamento casual'}
• Confronte com carinho quando necessário
• Celebre conquistas sempre
• Em crises, seja mais direto mas acolhedor
• NUNCA ignore o que o usuário acabou de dizer

DIRETRIZES:
1. Responda como padrinho/amigo, não como terapeuta
2. PRIORIZE a mensagem atual sobre contexto histórico
3. Use informações de diário/sobriedade apenas se relevante para a conversa atual
4. Sempre ofereça apoio prático relacionado ao que ele disse
5. Faça perguntas sobre o que ELE trouxe, não sobre outros assuntos
6. Se detectar crise, priorize apoio imediato

LEMBRE-SE: SEMPRE use "|||" para separar as mensagens!`;

    return `${personalityPrompt}\n\n${contextualInfo}\n${guidelines}`;
  }

  private prepareConversationHistory(context: ConversationContext): Array<{role: string, content: string}> {
    // Get last messages for better context, considering summarization.
    // We want to send as much relevant history as possible without exceeding token limits.
    // Let's aim for roughly half of the MAX_TOKENS_PER_REQUEST for the history part.
    // This is a heuristic and might need adjustment based on actual token usage.

    let currentTokenCount = 0;
    const historyForApi: Array<{role: string, content: string}> = [];
    const MAX_HISTORY_TOKENS = this.MAX_TOKENS_PER_REQUEST / 2; // Reserve half for system prompt and current message

    // Iterate through history in reverse to prioritize recent messages
    for (let i = context.conversationHistory.length - 1; i >= 0; i--) {
      const message = context.conversationHistory[i];
      const messageText = `${message.role}: ${message.content}`;
      const messageTokenCount = messageText.length; // Simple approximation of token count

      if (currentTokenCount + messageTokenCount <= MAX_HISTORY_TOKENS) {
        historyForApi.unshift(message); // Add to the beginning of the array to maintain order
        currentTokenCount += messageTokenCount;
      } else {
        // If adding this message exceeds the limit, stop.
        // This ensures we don't break the token limit.
        break;
      }
    }

    // Ensure the history is not empty and contains at least some context if possible.
    // If the history is too short to fill MAX_HISTORY_TOKENS, it will just contain what's available.

    return historyForApi.map(msg => ({
      role: msg.role === 'user' ? 'user' : 'assistant',
      content: msg.content
    }));
  }

  private detectRecentStruggle(context: ConversationContext): boolean {
    // Check recent messages for struggle indicators
    const recentMessages = context.conversationHistory.slice(-4);
    const struggleKeywords = ['difícil', 'vontade', 'tentação', 'crise', 'problema', 'triste', 'ansioso'];

    return recentMessages.some(msg =>
      msg.role === 'user' &&
      struggleKeywords.some(keyword =>
        msg.content.toLowerCase().includes(keyword)
      )
    );
  }

  private generateSuggestedActions(context: ConversationContext, response: ConversationResponse): string[] {
    const actions: string[] = [];

    if (response.supportType === 'crisis') {
      actions.push('Ligar para um amigo de confiança', 'Procurar ajuda profissional', 'Fazer exercício de respiração');
    } else if (response.supportType === 'encouragement') {
      actions.push('Anotar este momento', 'Compartilhar conquista', 'Definir próxima meta');
    } else if (response.supportType === 'advice') {
      actions.push('Salvar esta dica', 'Praticar sugestão', 'Buscar grupo de apoio');
    }

    // Add contextual actions
    if (context.sobrietyDays > 0 && context.sobrietyDays % 7 === 0) {
      actions.push('Celebrar marco de sobriedade');
    }

    if (!context.recentNotes || context.recentNotes.length === 0) {
      actions.push('Criar primeira nota');
    }

    return actions.slice(0, 3); // Limit to 3 suggestions
  }

  private extractMoodFromConversation(userMessage: string, assistantResponse: string): string | undefined {
    const moodIndicators = {
      'bem': 'positivo',
      'feliz': 'positivo',
      'animado': 'positivo',
      'triste': 'negativo',
      'ansioso': 'ansioso',
      'preocupado': 'ansioso',
      'difícil': 'desafiador',
      'cansado': 'neutro'
    };

    const lowerMessage = userMessage.toLowerCase();

    for (const [keyword, mood] of Object.entries(moodIndicators)) {
      if (lowerMessage.includes(keyword)) {
        return mood;
      }
    }

    return undefined;
  }

  private async getOrInitializeContext(userId: string): Promise<ConversationContext> {
    if (this.contexts.has(userId)) {
      return this.contexts.get(userId)!;
    }
    return await this.initializeContext(userId);
  }

  private async loadContext(userId: string): Promise<ConversationContext | null> {
    try {
      const stored = await AsyncStorage.getItem(`${this.CONTEXT_STORAGE_KEY}_${userId}`);
      if (stored) {
        const context = JSON.parse(stored);
        // Convert date strings back to Date objects
        context.lastInteraction = new Date(context.lastInteraction);
        context.conversationHistory = context.conversationHistory.map((msg: any) => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }));
        return context;
      }
    } catch (error) {
      safeLog.error('Error loading context:', error);
    }
    return null;
  }

  private async saveContext(context: ConversationContext): Promise<void> {
    try {
      await AsyncStorage.setItem(
        `${this.CONTEXT_STORAGE_KEY}_${context.userId}`,
        JSON.stringify(context)
      );
    } catch (error) {
      safeLog.error('Error saving context:', error);
    }
  }

  private isContextExpired(context: ConversationContext): boolean {
    if (!context.lastInteraction) return true;

    const now = new Date();
    const lastInteraction = new Date(context.lastInteraction);

    return (now.getTime() - lastInteraction.getTime()) > this.CONTEXT_TIMEOUT;
  }

  async clearContext(userId: string): Promise<void> {
    this.contexts.delete(userId);
    try {
      await AsyncStorage.removeItem(`${this.CONTEXT_STORAGE_KEY}_${userId}`);
    } catch (error) {
      safeLog.error('Error clearing context:', error);
    }
  }

  async getConversationHistory(userId: string): Promise<ConversationMessage[]> {
    const context = await this.getOrInitializeContext(userId);
    return context.conversationHistory;
  }

  // --- Meeting-related helper methods ---

  isMeetingRelatedQuery(message: string): boolean {
    const meetingKeywords = [
      'reuniões', 'reunião', 'encontros', 'encontro', 'meetings', 'meeting',
      'próximas reuniões', 'próximos encontros', 'quando', 'horários',
      'que horas', 'grupos', 'grupo', 'aa', 'alcoólicos anônimos',
      'na', 'narcóticos anônimos', 'schedule', 'horário'
    ];

    const lowerMessage = message.toLowerCase();
    return meetingKeywords.some(keyword => lowerMessage.includes(keyword));
  }

  async handleMeetingQuery(userId: string, message: string): Promise<any> {
    try {
      safeLog.info('🤝 ConversationService: Handling meeting query', { userId, message: message.substring(0, 50) + '...' });

      // Get all meetings
      const allMeetings = await MeetingsService.getAllMeetings();
      safeLog.info('📋 ConversationService: Loaded meetings', { count: allMeetings.length });

      // Get next 3 upcoming meetings
      const upcomingMeetings = this.getNextThreeMeetings(allMeetings);
      safeLog.info('⏰ ConversationService: Found upcoming meetings', { count: upcomingMeetings.length });

      // Format meetings data for AI
      const meetingsInfo = upcomingMeetings.map(meeting => {
        const days = meeting.meeting_weekday.split(';').map(d => d.trim()).join(', ');
        return {
          name: meeting.meeting_name,
          time: meeting.meeting_time,
          days: days,
          category: meeting.meeting_category,
          place: meeting.meeting_place,
          region: meeting.meeting_region
        };
      });

      return {
        meetings: meetingsInfo,
        totalAvailable: allMeetings.length,
        nextThree: upcomingMeetings.length
      };

    } catch (error) {
      safeLog.error('❌ ConversationService: Error handling meeting query:', error);
      return null;
    }
  }

  getNextThreeMeetings(allMeetings: Meeting[]): Meeting[] {
    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes(); // Current time in minutes
    const currentWeekday = this.getDayName(now.getDay());

    // Create an array of meetings with their next occurrence time
    const meetingsWithNextTime = allMeetings.map(meeting => {
      const meetingTime = this.parseTimeToMinutes(meeting.meeting_time);
      const meetingDays = meeting.meeting_weekday.split(';').map(day => day.trim());

      let nextOccurrence = Infinity;

      // Check each day the meeting occurs
      meetingDays.forEach(day => {
        const dayIndex = this.getDayIndex(day);
        const currentDayIndex = now.getDay();

        let daysUntil = (dayIndex - currentDayIndex + 7) % 7;

        // If it's today and the meeting hasn't happened yet
        if (daysUntil === 0 && meetingTime > currentTime) {
          nextOccurrence = Math.min(nextOccurrence, meetingTime);
        }
        // If it's not today or the meeting has already happened today
        else if (daysUntil > 0 || (daysUntil === 0 && meetingTime <= currentTime)) {
          if (daysUntil === 0) daysUntil = 7; // Next week if meeting already happened today
          const timeUntilMeeting = (daysUntil * 24 * 60) + meetingTime;
          nextOccurrence = Math.min(nextOccurrence, timeUntilMeeting);
        }
      });

      return {
        ...meeting,
        nextOccurrence
      };
    });

    // Sort by next occurrence and take the first 3
    return meetingsWithNextTime
      .filter(meeting => meeting.nextOccurrence !== Infinity)
      .sort((a, b) => a.nextOccurrence - b.nextOccurrence)
      .slice(0, 3);
  }

  parseTimeToMinutes(timeString: string): number {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  }

  getDayName(dayIndex: number): string {
    const days = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];
    return days[dayIndex];
  }

  getDayIndex(dayName: string): number {
    const dayMap: { [key: string]: number } = {
      'Domingo': 0,
      'Segunda-feira': 1,
      'Terça-feira': 2,
      'Quarta-feira': 3,
      'Quinta-feira': 4,
      'Sexta-feira': 5,
      'Sábado': 6
    };
    return dayMap[dayName] || 0;
  }

  extractSearchQuery(message: string): string | undefined {
    // Simple extraction - could be enhanced with NLP
    const searchPatterns = [
      /procurar por (.+)/i,
      /buscar (.+)/i,
      /encontrar (.+)/i,
    ];

    for (const pattern of searchPatterns) {
      const match = message.match(pattern);
      if (match) {
        return match[1].trim();
      }
    }

    // If no pattern matches but it seems like a search query
    if (message.length > 10 && !message.includes('?')) {
      return message;
    }
    return undefined;
  }

  // --- End of Meeting-related helper methods ---

}

export default ConversationService.getInstance();